import merge from "lodash/merge";

/**
 * Element-wise array merge utility. Matches elements between `existing`
 * and `incoming` using `isSame`, deep-merges matched pairs, and appends
 * unmatched incoming elements. Useful for building complete arrays from
 * sparse diffs before passing them to `queueChange`.
 *
 * @example
 * const updatedCarriers = mergeArrayById(
 *   payload.carriers,
 *   [{ id: "carrier-1", hidden: true }],
 *   (a, b) => a.id === b.id,
 * );
 * queueChange({ carriers: updatedCarriers });
 */
export const mergeArrayById = <T>(
  existing: readonly T[],
  incoming: readonly Partial<T>[],
  isSame: (a: T, b: Partial<T>) => boolean,
): T[] => {
  const result: T[] = existing.map((element) => {
    const match = incoming.find((inc) => isSame(element, inc));

    return match ? merge({}, element, match) : element;
  });

  for (const inc of incoming) {
    const exists = existing.some((element) => isSame(element, inc));
    if (!exists) {
      result.push(inc as T);
    }
  }

  return result;
};
